/**
 * Belt Builder Controller
 * 5-step flow: Belt → Pouch → Utility → Loops → Fittings (review in cart)
 * Collections: builder-belts, dominant-hand-pouches, non-dominant-hand-pouches, builder-loops, builder-accessories
 * Pouch variants auto-selected by belt Orientation (Left Handed / Right Handed).
 */

if (typeof window !== 'undefined' && window.console?.log) {
  console.log('[BB] belt-builder.js loaded (variant availability + debug build)');
}

const BB_DEBUG_AVAILABILITY = true;

const STEPS = ['belt', 'pouch', 'utility', 'loops', 'fittings'];
const STEP_LABELS = { belt: 'BELT', pouch: 'POUCH', utility: 'UTILITY', loops: 'LOOPS', fittings: 'FITTINGS' };

const COLLECTION_HANDLES = {
  belts: 'builder-belts',
  primaryPouches: 'dominant-hand-pouches',
  secondaryPouches: 'non-dominant-hand-pouches',
  loops: 'builder-loops',
  accessories: 'builder-accessories'
};

const POUCH_OPTION_WHITELIST = {
  primary: ['Top Pocket', 'Diagonal Pica Loops'],
  secondary: ['Top Pocket', 'Cats Paw Sheath']
};

const POUCH_OPTION_VALUES = {
  'Top Pocket': ['Bit Pocket', 'Open Tape Pocket'],
  'Diagonal Pica Loops': ['Standard', 'Diagonal Pica Loops'],
  'Cats Paw Sheath': ['Single Cats Paw', 'Double Cats Paw']
};

const ORIENTATION_OPTION_NAMES = ['Orientation', 'Side', 'Hand'];

function normalize(value) {
  return (value || '').toLowerCase().trim();
}

class BeltBuilder {
  constructor({ mount, moneyFormat }) {
    this.mount = mount;
    this.moneyFormat = moneyFormat || '{{amount}}';

    this.products = {
      belts: [],
      primaryPouches: [],
      secondaryPouches: [],
      loops: [],
      accessories: []
    };

    this.state = {
      baseBelt: { variantId: null, orientation: null, size: null },
      primaryPouch: null,
      secondaryPouch: null,
      loops: [],
      fittings: []
    };

    this.ui = {
      stage: 'belt',
      activeMainImageSrc: null,
      activeThumbIndex: 0,
      isInfoOpen: false,
      infoProduct: null,
      infoActiveImageIndex: 0,
      isMeasureDrawerOpen: false,
      lastClickedLoopProductId: null,
      lastClickedFittingProductId: null,
      showStepHint: false,
      cartError: null
    };
    this._stepHintTimer = null;
  }

  async init() {
    this.ui.cartError = null;
    this.mount.innerHTML = '<p class="belt-builder__loading">Loading belt builder…</p>';
    await this.loadProducts();
    this.attachEventListeners();
    this.render();
  }

  async loadProducts() {
    const root = (typeof window !== 'undefined' && window.Shopify?.routes?.root) ? window.Shopify.routes.root : '/';
    const base = root.replace(/\/$/, '');

    const toProduct = (product) => {
      const imagesRaw = product.images || [];
      const images = imagesRaw.map((img) => (typeof img === 'string' ? img : img?.src || img)).filter(Boolean);
      const imagesWithVariantIds = imagesRaw.map((img) => {
        if (typeof img === 'string') return { src: img, variant_ids: [] };
        const src = img?.src || img;
        if (!src) return null;
        const variantIds = Array.isArray(img?.variant_ids) ? img.variant_ids : [];
        return { src, variant_ids: variantIds };
      }).filter(Boolean);
      const parsePriceToCents = (price) => {
        const p = parseFloat(price);
        if (Number.isNaN(p)) return 0;
        const str = String(price).trim();
        const hasDecimal = str.includes('.');
        if (hasDecimal) return Math.round(p * 100);
        if (p >= 1000) return Math.round(p);
        return Math.round(p * 100);
      };
      return {
        productId: product.id,
        title: product.title,
        handle: product.handle,
        descriptionHtml: product.body_html || product.description || '',
        image: images[0] || product.images?.[0]?.src,
        images,
        imagesWithVariantIds,
        options: product.options || [],
        variants: (product.variants || []).map((v) => {
          const priceCents = parsePriceToCents(v.price);
          return {
          variantId: v.id,
          title: v.title,
          price: priceCents / 100,
          priceCents,
          option1: v.option1,
          option2: v.option2,
          option3: v.option3,
          image: v.featured_image?.src || (typeof v.featured_image === 'string' ? v.featured_image : null) || null,
          available: v.available ?? v.availableForSale,
          inventory_quantity: v.inventory_quantity,
          overlayImageLeft: v.overlay_image_left_url || null,
          overlayImageRight: v.overlay_image_right_url || null
        };
      }),
      };
    };

    const primaryPouchesEl = document.getElementById('belt-builder-primary-pouches-json');
    const secondaryPouchesEl = document.getElementById('belt-builder-secondary-pouches-json');
    const loopsEl = document.getElementById('belt-builder-loops-json');
    const accessoriesEl = document.getElementById('belt-builder-accessories-json');

    let primaryPouchesData = null;
    let secondaryPouchesData = null;
    if (primaryPouchesEl?.textContent?.trim()) {
      try {
        const parsed = JSON.parse(primaryPouchesEl.textContent);
        primaryPouchesData = Array.isArray(parsed) ? parsed : [];
      } catch (_) { /* fallback to fetch */ }
    }
    if (secondaryPouchesEl?.textContent?.trim()) {
      try {
        const parsed = JSON.parse(secondaryPouchesEl.textContent);
        secondaryPouchesData = Array.isArray(parsed) ? parsed : [];
      } catch (_) { /* fallback to fetch */ }
    }

    const fetchPromises = [
      fetch(`${base}/collections/${COLLECTION_HANDLES.belts}/products.json`),
      primaryPouchesData === null ? fetch(`${base}/collections/${COLLECTION_HANDLES.primaryPouches}/products.json`) : Promise.resolve(null),
      secondaryPouchesData === null ? fetch(`${base}/collections/${COLLECTION_HANDLES.secondaryPouches}/products.json`) : Promise.resolve(null),
      fetch(`${base}/collections/${COLLECTION_HANDLES.loops}/products.json`),
      fetch(`${base}/collections/${COLLECTION_HANDLES.accessories}/products.json`)
    ];
    const [beltsRes, primaryPouchesRes, secondaryPouchesRes, loopsRes, accessoriesRes] = await Promise.all(fetchPromises);

    const beltsData = await beltsRes.json();
    let loopsData = await loopsRes.json();
    let accessoriesData = await accessoriesRes.json();

    if (loopsEl?.textContent?.trim()) {
      try {
        const parsed = JSON.parse(loopsEl.textContent);
        loopsData = Array.isArray(parsed) ? parsed : { products: parsed };
      } catch (_) { /* fallback to fetch data */ }
    }
    if (accessoriesEl?.textContent?.trim()) {
      try {
        const parsed = JSON.parse(accessoriesEl.textContent);
        accessoriesData = Array.isArray(parsed) ? parsed : { products: parsed };
      } catch (_) { /* fallback to fetch data */ }
    }

    this.products.belts = (beltsData.products || beltsData).map(toProduct);

    const baseImagesEl = document.getElementById('belt-builder-base-images-json');
    if (baseImagesEl?.textContent) {
      try {
        const baseImagesMap = JSON.parse(baseImagesEl.textContent);
        this.products.belts.forEach((p) => {
          p.builderBaseImage = baseImagesMap[String(p.productId)] || null;
        });
      } catch (_) { /* ignore parse errors */ }
    }

    const sizeImagesEl = document.getElementById('belt-builder-size-images-json');
    if (sizeImagesEl?.textContent) {
      try {
        const sizeImagesMap = JSON.parse(sizeImagesEl.textContent);
        this.products.belts.forEach((p) => {
          const entry = sizeImagesMap[String(p.productId)];
          p.builderSizeImages = entry && typeof entry === 'object' ? entry : null;
        });
      } catch (_) { /* ignore parse errors */ }
    }

    if (primaryPouchesData !== null) {
      this.products.primaryPouches = primaryPouchesData.map(toProduct);
    } else {
      const primaryPouchesDataFetched = await primaryPouchesRes.json();
      this.products.primaryPouches = (primaryPouchesDataFetched.products || primaryPouchesDataFetched).map(toProduct);
    }
    if (secondaryPouchesData !== null) {
      this.products.secondaryPouches = secondaryPouchesData.map(toProduct);
    } else {
      const secondaryPouchesDataFetched = await secondaryPouchesRes.json();
      this.products.secondaryPouches = (secondaryPouchesDataFetched.products || secondaryPouchesDataFetched).map(toProduct);
    }

    this.products.loops = (loopsData.products || loopsData).map(toProduct);
    this.products.accessories = (accessoriesData.products || accessoriesData).map(toProduct);

    if (BB_DEBUG_AVAILABILITY && typeof window !== 'undefined' && window.console?.log) {
      const allPouches = [...(this.products.primaryPouches || []), ...(this.products.secondaryPouches || [])];
      const wardenRH = allPouches.find((p) => (p.title || '').toLowerCase().includes('warden') && ((p.title || '').toLowerCase().includes('rh') || (p.title || '').toLowerCase().includes('right')));
      if (wardenRH) {
        console.log('[BB] T1 Warden RH product data (runtime verification)', {
          productId: wardenRH.productId,
          title: wardenRH.title,
          optionNames: (wardenRH.options || []).map((o) => typeof o === 'string' ? o : o?.name),
          variants: (wardenRH.variants || []).map((v) => ({
            variantId: v.variantId,
            option1: v.option1,
            option2: v.option2,
            option3: v.option3,
            available: v.available,
            inventory_quantity: v.inventory_quantity
          }))
        });
      }
    }

    console.log('Belt builder loaded products:', {
      belts: this.products.belts.length,
      primaryPouches: this.products.primaryPouches.length,
      secondaryPouches: this.products.secondaryPouches.length,
      loops: this.products.loops.length,
      accessories: this.products.accessories.length
    });
  }

  get dominantSide() {
    const o = this.state.baseBelt.orientation;
    return o === 'RIGHT HANDED' ? 'RH' : o === 'LEFT HANDED' ? 'LH' : null;
  }

  get primarySide() {
    return this.dominantSide;
  }

  get secondarySide() {
    return this.dominantSide === 'RH' ? 'LH' : this.dominantSide === 'LH' ? 'RH' : null;
  }

  get flattenedBeltVariants() {
    const variants = [];
    (this.products.belts || []).forEach((p) => {
      (p.variants || []).forEach((v) => variants.push({ ...v, productId: p.productId, productImage: p.image }));
    });
    return variants;
  }

  get firstBeltProductImage() {
    const first = this.products.belts?.[0];
    return first?.builderBaseImage || first?.image || first?.variants?.[0]?.image || null;
  }

  getSelectedBeltProduct() {
    const baseVariant = this.getBaseVariant();
    if (!baseVariant?.productId) return null;
    return (this.products.belts || []).find((p) => String(p.productId) === String(baseVariant.productId)) || null;
  }

  get baseBeltImageUrl() {
    const url = this.mount?.dataset?.baseBeltImage;
    return url || this.firstBeltProductImage;
  }

  get lowestBeltPriceCents() {
    const vs = this.flattenedBeltVariants;
    if (!vs.length) return 0;
    return Math.min(...vs.map((v) => v.priceCents));
  }

  get allBeltSizeValues() {
    const vs = this.flattenedBeltVariants;
    return [...new Set(vs.map((v) => v.option2).filter(Boolean))];
  }

  get builderSizePreviewPng() {
    if (this.ui.stage !== 'belt') return null;
    const selectedBelt = this.getSelectedBeltProduct();
    const size = this.state.baseBelt.size;
    if (!selectedBelt || !size) return null;
    const map = selectedBelt.builderSizeImages || null;
    if (!map) return null;
    return map[size] || null;
  }

  canNavigateToStep(step) {
    const targetIndex = STEPS.indexOf(step);
    const currentIndex = STEPS.indexOf(this.ui.stage);
    if (targetIndex < 0) return false;

    if (targetIndex <= currentIndex) return true;

    for (let i = 0; i < targetIndex; i++) {
      if (!this.getStepCompletion(STEPS[i]).complete) return false;
    }
    return true;
  }

  isProductOutOfStock(product) {
    if (!product?.variants?.length) return true;
    return product.variants.every((v) => {
      if (v.available === false) return true;
      if (typeof v.inventory_quantity === 'number' && v.inventory_quantity <= 0) return true;
      return false;
    });
  }

  isVariantAvailable(variant) {
    if (!variant) return false;
    if (variant.available === false) return false;
    if (typeof variant.inventory_quantity === 'number' && variant.inventory_quantity <= 0) return false;
    return true;
  }

  /**
   * Returns true if every variant of the pouch that matches the given orientation
   * is unavailable (available === false or inventory_quantity <= 0).
   * Used to show OUT OF STOCK and disable the pouch product card when the current
   * belt orientation has no in-stock variants for that product.
   * @param {Object} product - Pouch product with variants and options
   * @param {string} orientation - "Right Handed" or "Left Handed"
   */
  isPouchOutOfStockForOrientation(product, orientation) {
    if (!product?.variants?.length) return true;
    const orientOpt = ORIENTATION_OPTION_NAMES.find((n) => this.getOptionPosition(product, n));
    if (!orientOpt) return this.isProductOutOfStock(product);
    const pos = this.getOptionPosition(product, orientOpt);
    if (!pos) return this.isProductOutOfStock(product);
    const variantsForOrientation = product.variants.filter(
      (v) => normalize(this.getVariantOptionValue(v, pos)) === normalize(orientation)
    );
    if (variantsForOrientation.length === 0) return true;
    return variantsForOrientation.every((v) => !this.isVariantAvailable(v));
  }

  /**
   * For a given option value (e.g. "Diagonal Pica Loops"), returns whether the variant
   * that would be selected (current pouch selections with this option set to value) is available.
   */
  isOptionValueAvailable(product, stateKey, optionName, value) {
    const selections = { ...this.getPouchSelectionsForProduct(stateKey, product), [optionName]: value };
    const variant = this.resolveVariantBySelections(product, selections);
    const available = variant != null && this.isVariantAvailable(variant);
    if (BB_DEBUG_AVAILABILITY && typeof window !== 'undefined' && window.console?.log) {
      console.log('[BB] isOptionValueAvailable', {
        productTitle: product?.title,
        productId: product?.productId,
        stateKey,
        optionName,
        value,
        selections: { ...selections },
        variantId: variant?.variantId ?? null,
        variantAvailable: variant?.available,
        variantInventoryQuantity: variant?.inventory_quantity,
        result: available
      });
    }
    return available;
  }

  getOptionPosition(product, optionName) {
    if (!product?.options?.length || !optionName) return null;
    const opts = product.options;
    for (let i = 0; i < opts.length; i++) {
      const o = opts[i];
      const name = typeof o === 'string' ? o : (o?.name || '');
      if (normalize(name) === normalize(optionName)) return (o?.position ?? i + 1);
    }
    return null;
  }

  getVariantOptionValue(variant, position) {
    if (!variant || !position) return null;
    const val = variant[`option${position}`];
    return val != null ? String(val).trim() : null;
  }

  resolveVariantBySelections(product, selectionsByName) {
    if (!product?.variants?.length) return null;
    const opts = product.options || [];
    const nameToPos = {};
    opts.forEach((o, i) => {
      const name = (typeof o === 'string' ? o : (o?.name || '')).trim();
      if (name) nameToPos[normalize(name)] = o?.position ?? i + 1;
    });

    const variant = product.variants.find((v) => {
      return Object.entries(selectionsByName).every(([name, value]) => {
        const pos = nameToPos[normalize(name)];
        if (!pos || value == null || value === '') return true;
        const vVal = this.getVariantOptionValue(v, pos);
        return normalize(vVal) === normalize(value);
      });
    }) || null;
    if (BB_DEBUG_AVAILABILITY && typeof window !== 'undefined' && window.console?.log) {
      console.log('[BB] resolveVariantBySelections', {
        productTitle: product?.title,
        selectionsByName: { ...selectionsByName },
        variantId: variant?.variantId ?? null,
        available: variant?.available,
        inventory_quantity: variant?.inventory_quantity,
        found: !!variant
      });
    }
    return variant;
  }

  getVisibleOptionValues(product, optionName) {
    const pos = this.getOptionPosition(product, optionName);
    if (!pos) return [];
    const allowed = POUCH_OPTION_VALUES[optionName];
    if (!allowed?.length) return [];
    const fromVariants = [...new Set(
      (product.variants || [])
        .map((v) => this.getVariantOptionValue(v, pos))
        .filter(Boolean)
    )];
    return allowed.filter((a) => fromVariants.some((f) => normalize(f) === normalize(a)));
  }

  getVariantForOrientation(product, orientationValue) {
    return this.resolveVariantBySelections(product, { Orientation: orientationValue });
  }

  get primaryOrientation() {
    const o = this.state.baseBelt.orientation;
    return o === 'RIGHT HANDED' ? 'Right Handed' : o === 'LEFT HANDED' ? 'Left Handed' : null;
  }

  get secondaryOrientation() {
    const o = this.state.baseBelt.orientation;
    return o === 'RIGHT HANDED' ? 'Left Handed' : o === 'LEFT HANDED' ? 'Right Handed' : null;
  }

  getPouchSelectionsForProduct(stateKey, product) {
    const orientation = stateKey === 'primaryPouch' ? this.primaryOrientation : this.secondaryOrientation;
    const sel = this.state[stateKey];
    const selections = {};
    if (orientation && product) {
      const orientOpt = ORIENTATION_OPTION_NAMES.find((n) => this.getOptionPosition(product, n));
      if (orientOpt) selections[orientOpt] = orientation;
    } else if (orientation) {
      selections.Orientation = orientation;
    }
    if (!product || !sel) return selections;
    const allowed = POUCH_OPTION_WHITELIST[stateKey === 'primaryPouch' ? 'primary' : 'secondary'];
    allowed.forEach((optName) => {
      const val = sel[this.optionNameToStateKey(optName)];
      if (val) selections[optName] = val;
    });
    return selections;
  }

  /**
   * Returns reference selections for thumbnail lookup. Uses index-based alternation
   * so each option tile maps to a unique variant (avoids duplicate images in 2x2 grids).
   * Falls back to try-each-value if index-based ref does not resolve.
   */
  getReferenceSelectionForThumbnail(product, optionName, optionValue, stateKey, orientation) {
    const ref = {};
    const allowed = POUCH_OPTION_WHITELIST[stateKey === 'primaryPouch' ? 'primary' : 'secondary'];
    const otherOpts = allowed.filter((opt) => opt !== optionName);

    const orientOpt = ORIENTATION_OPTION_NAMES.find((n) => this.getOptionPosition(product, n));

    const currentValues = this.getVisibleOptionValues(product, optionName);
    const valueIndex = currentValues.findIndex((v) => normalize(v) === normalize(optionValue));
    const optionIndexInWhitelist = allowed.indexOf(optionName);

    for (const otherOpt of otherOpts) {
      const pos = this.getOptionPosition(product, otherOpt);
      if (!pos) continue;
      const siblingValues = this.getVisibleOptionValues(product, otherOpt);
      if (!siblingValues.length) continue;

      let candidate;
      if (valueIndex >= 0 && siblingValues.length === 2 && otherOpts.length === 1) {
        const refIndex = optionIndexInWhitelist === 0 ? valueIndex : 1 - valueIndex;
        const clampedIndex = Math.max(0, Math.min(refIndex, siblingValues.length - 1));
        candidate = siblingValues[clampedIndex];
      } else {
        candidate = siblingValues[0];
      }

      let testSelections = { ...ref, [optionName]: optionValue, [otherOpt]: candidate };
      if (orientOpt && orientation) testSelections[orientOpt] = orientation;
      let variant = this.resolveVariantBySelections(product, testSelections);

      if (!variant) {
        for (const c of siblingValues) {
          testSelections = { ...ref, [optionName]: optionValue, [otherOpt]: c };
          if (orientOpt && orientation) testSelections[orientOpt] = orientation;
          variant = this.resolveVariantBySelections(product, testSelections);
          if (variant) {
            candidate = c;
            break;
          }
        }
      }

      ref[otherOpt] = candidate;
    }
    return ref;
  }

  /**
   * Dev-only: warns if two or more tiles in the same option group resolve to the same variantId.
   */
  addDuplicateVariantWarning(optionName, values, valueToVariantId) {
    if (typeof console === 'undefined' || !console.warn) return;
    const byVariantId = {};
    values.forEach((v) => {
      const vid = valueToVariantId[v];
      if (vid != null) {
        if (!byVariantId[vid]) byVariantId[vid] = [];
        byVariantId[vid].push(v);
      }
    });
    const duplicates = Object.values(byVariantId).filter((arr) => arr.length > 1);
    if (duplicates.length) {
      console.warn('[BeltBuilder] Option tiles in same group resolve to same variantId:', optionName, duplicates);
    }
  }

  /**
   * Get the best image for a variant. Prefers product.imagesWithVariantIds entry whose
   * variant_ids includes this variantId (Shopify's canonical mapping). Falls back to
   * variant.image (featured_image) then product.image.
   */
  getImageForVariant(product, variant, variantId) {
    const vid = variantId ?? variant?.variantId;
    if (!product || vid == null) return null;

    const imagesWithIds = product.imagesWithVariantIds || product.images || [];
    const imgWithVariant = imagesWithIds.find((img) => {
      const ids = img?.variant_ids;
      if (!Array.isArray(ids)) return false;
      return ids.some((id) => String(id) === String(vid));
    });
    if (imgWithVariant) {
      const src = typeof imgWithVariant === 'string' ? imgWithVariant : imgWithVariant?.src;
      return src || null;
    }

    const hasVariantIds = imagesWithIds.some((img) => Array.isArray(img?.variant_ids) && img.variant_ids.length > 0);
    if (hasVariantIds && typeof console !== 'undefined' && console.warn) {
      console.warn('[BeltBuilder] No variant_ids match for variant', vid, '- using featured_image/product fallback. Assign this variant to an image in Shopify Admin to fix.');
    }

    if (variant?.image) return variant.image;
    return product.image || null;
  }

  findFirstVariantWithImage(product, selectionsByName) {
    if (!product?.variants?.length) return null;
    const variant = this.resolveVariantBySelections(product, selectionsByName);
    if (!variant) return null;
    return this.getImageForVariant(product, variant, variant.variantId);
  }

  /**
   * Returns { image, variantId } for an option tile. Prefers shared builder option images
   * (window.BELT_BUILDER_OPTION_IMAGES) when the question matches; otherwise uses variant image.
   */
  getOptionCardVariantImage(product, orientation, optionName, optionValue, stateKey) {
    if (!product?.variants?.length) return { image: product?.image || null, variantId: null };
    const orientOpt = ORIENTATION_OPTION_NAMES.find((n) => this.getOptionPosition(product, n));
    const base = orientOpt && orientation ? { [orientOpt]: orientation } : {};
    const ref = this.getReferenceSelectionForThumbnail(product, optionName, optionValue, stateKey, orientation);
    const selections = { ...base, ...ref, [optionName]: optionValue };
    const variant = this.resolveVariantBySelections(product, selections);

    let img = null;
    const optionImages = typeof window !== 'undefined' && window.BELT_BUILDER_OPTION_IMAGES;
    if (optionImages && optionName && optionValue != null) {
      if (optionName === 'Top Pocket' && optionImages.topPocket) {
        img = optionImages.topPocket[optionValue] || null;
      } else if (optionName === 'Diagonal Pica Loops' && optionImages.diagonalPicaLoops) {
        img = optionImages.diagonalPicaLoops[optionValue] || null;
      } else if (optionName === 'Cats Paw Sheath' && optionImages.catsPawSheath) {
        img = optionImages.catsPawSheath[optionValue] || null;
      }
    }
    if (!img && variant) {
      img = this.getImageForVariant(product, variant, variant.variantId);
    }
    return {
      image: img || product?.image || null,
      variantId: variant?.variantId ?? null
    };
  }

  optionNameToStateKey(name) {
    const map = { 'Top Pocket': 'topPocket', 'Diagonal Pica Loops': 'diagonalPicaLoops', 'Cats Paw Sheath': 'catsPawSheath' };
    return map[name] || name;
  }

  /**
   * Get overlay URL for pouch preview from variant metafields by belt orientation.
   * Left Handed => overlay_image_left; otherwise => overlay_image_right.
   * No fallback to variant.image or product.image.
   */
  getOverlayImageForVariant(variant) {
    if (!variant) return null;
    const isLeft = this.state.baseBelt.orientation === 'LEFT HANDED';
    return isLeft ? (variant.overlayImageLeft || null) : (variant.overlayImageRight || null);
  }

  /**
   * Get first available overlay image for a pouch product for current belt orientation.
   * Used to show pouch in preview immediately on card selection (before options are chosen).
   */
  getFirstOverlayImageForProduct(product) {
    if (!product?.variants?.length) return null;
    const isLeft = this.state.baseBelt.orientation === 'LEFT HANDED';
    for (const v of product.variants) {
      const url = isLeft ? (v.overlayImageLeft || null) : (v.overlayImageRight || null);
      if (url) return url;
    }
    return null;
  }

  syncPouchVariantsOnOrientationChange() {
    const sync = (stateKey, productList) => {
      const sel = this.state[stateKey];
      if (!sel?.productId) return;
      const product = (productList || []).find((p) => String(p.productId) === String(sel.productId));
      if (!product) return;
      const selections = this.getPouchSelectionsForProduct(stateKey, product);
      const variant = this.resolveVariantBySelections(product, selections);
      if (variant) {
        const overlayImage = this.getOverlayImageForVariant(variant);
        this.state[stateKey] = {
          ...sel,
          variantId: variant.variantId,
          title: product.title,
          priceCents: variant.priceCents,
          image: variant.image || product.image,
          overlayImage: overlayImage || null
        };
      } else {
        this.state[stateKey] = { ...sel, variantId: null, overlayImage: null };
      }
    };
    sync('primaryPouch', this.products.primaryPouches);
    sync('secondaryPouch', this.products.secondaryPouches);
  }

  render() {
    if (typeof window !== 'undefined' && console?.log) {
      console.log('[BB] render', { fn: 'render', stage: this.ui.stage, reason: this._lastAction || 'unknown', willReplacePanel: true, willReplacePreview: true, willReplaceFooter: true });
    }
    const { orientation, size } = this.state.baseBelt;
    const variant = this.findVariant(orientation, size);
    this.state.baseBelt.variantId = variant ? variant.id : null;

    const scrollEl = this.mount.querySelector('.belt-builder__left-panel-scroll');
    const savedScrollTop = scrollEl ? scrollEl.scrollTop : 0;

    this.teardownMobileFooterObserver();

    this.mount.innerHTML = `
      <div class="belt-builder">
        <div class="belt-builder__layout">
          <div class="belt-builder__sticky-top">
            <div class="belt-builder__right-panel">
              <div class="belt-builder__preview-area">
                ${this.renderPreview()}
              </div>
            </div>
            <nav class="belt-builder__breadcrumbs" role="navigation">
              ${this.renderBreadcrumbs()}
            </nav>
          </div>
          <aside class="belt-builder__left-panel">
            <div class="belt-builder__left-panel-scroll">
              <div class="belt-builder__panel-content">
                ${this.renderStepContent()}
              </div>
              <div class="belt-builder__footer-sentinel" aria-hidden="true"></div>
            </div>
            <div class="belt-builder__left-panel-footer belt-builder__footer">
              <div class="belt-builder__price">
                <span class="belt-builder__price-label">PRICE INCL. VAT (21%)</span>
                <span class="belt-builder__price-value">${this.formatMoney(this.getTotalPriceCents())}</span>
              </div>
              <div class="belt-builder__footer-actions">
                ${this.renderContinueButton()}
              </div>
            </div>
          </aside>
        </div>
      </div>
    `;

    const newScrollEl = this.mount.querySelector('.belt-builder__left-panel-scroll');
    if (newScrollEl) newScrollEl.scrollTop = savedScrollTop;

    this.setupMobileFooterObserver();

    const footerActions = this.mount.querySelector('.belt-builder__footer-actions');
    if (footerActions) this.updateFooterCtaInPlace(footerActions);
  }

  updatePreviewOnly() {
    const builderBase = this.builderBasePreviewPng;
    const showOverlays = this.ui.activeMainImageSrc === builderBase && this.ui.stage !== 'belt';

    const baseImg = this.mount.querySelector('.belt-builder__preview-img--base');
    if (baseImg && this.ui.activeMainImageSrc) {
      baseImg.src = this.ui.activeMainImageSrc;
    }
    const primaryImg = this.mount.querySelector('.belt-builder__preview-img--primary');
    const secondaryImg = this.mount.querySelector('.belt-builder__preview-img--secondary');
    const primaryOverlaySrc = this.state.primaryPouch?.overlayImage || '';
    const secondaryOverlaySrc = this.state.secondaryPouch?.overlayImage || '';
    if (primaryImg) {
      const showPrimary = showOverlays && primaryOverlaySrc;
      primaryImg.style.display = showPrimary ? '' : 'none';
      if (!primaryOverlaySrc) {
        primaryImg.src = '';
      } else if ((primaryImg.getAttribute('src') || '') !== primaryOverlaySrc) {
        this._setOverlaySrcWhenLoaded(primaryImg, primaryOverlaySrc);
      }
    }
    if (secondaryImg) {
      const showSecondary = showOverlays && secondaryOverlaySrc;
      secondaryImg.style.display = showSecondary ? '' : 'none';
      if (!secondaryOverlaySrc) {
        secondaryImg.src = '';
      } else if ((secondaryImg.getAttribute('src') || '') !== secondaryOverlaySrc) {
        this._setOverlaySrcWhenLoaded(secondaryImg, secondaryOverlaySrc);
      }
    }

    const thumbBtns = this.mount.querySelectorAll('.belt-builder__preview-thumbs [data-action="select-thumb"]');
    thumbBtns.forEach((btn, i) => {
      btn.classList.toggle('belt-builder__thumb--active', i === this.ui.activeThumbIndex);
    });
  }

  /**
   * Update footer CTA in place when possible to avoid innerHTML reflow.
   * On pouch/utility/fittings: only toggle disabled and hint visibility; no DOM replace.
   */
  updateFooterCtaInPlace(footerActions) {
    if (!footerActions) return;
    const btn = footerActions.querySelector('.belt-builder__continue');
    if (!btn) {
      footerActions.innerHTML = this.renderContinueButton();
      return;
    }
    const canProceed = this.canProceedToNextStage();
    btn.disabled = !canProceed;

    const hintEl = footerActions.querySelector('.belt-builder__footer-hint');
    if (hintEl) {
      const showHint = this.ui.showStepHint && (this.ui.stage === 'pouch' || this.ui.stage === 'utility');
      const hintMessage = this.ui.stage === 'pouch' ? 'Please complete pouch setup to continue' : 'Please complete utility pouch setup to continue';
      hintEl.style.display = showHint ? 'block' : 'none';
      hintEl.textContent = showHint ? hintMessage : '';
      hintEl.setAttribute('aria-hidden', showHint ? 'false' : 'true');
    }

    const overlay = footerActions.querySelector('.belt-builder__continue-overlay');
    if (overlay) {
      const showOverlay = !canProceed && (this.ui.stage === 'pouch' || this.ui.stage === 'utility');
      overlay.style.display = showOverlay ? 'block' : 'none';
      overlay.style.pointerEvents = showOverlay ? 'auto' : 'none';
      overlay.setAttribute('aria-hidden', showOverlay ? 'false' : 'true');
    }
  }

  /**
   * Swap overlay img src only after new image has loaded to prevent decode flash.
   */
  _setOverlaySrcWhenLoaded(imgEl, newSrc) {
    if (!imgEl || !newSrc) {
      if (imgEl) imgEl.src = newSrc || '';
      return;
    }
    const preload = new Image();
    preload.onload = () => {
      imgEl.src = newSrc;
    };
    preload.onerror = () => {
      imgEl.src = newSrc;
    };
    preload.src = newSrc;
  }

  updateBeltStepOnly() {
    const { orientation, size } = this.state.baseBelt;
    const variant = this.findVariant(orientation, size);
    this.state.baseBelt.variantId = variant ? variant.id : null;

    const panelContent = this.mount.querySelector('.belt-builder__panel-content');
    const priceEl = this.mount.querySelector('.belt-builder__price-value');
    const footerActions = this.mount.querySelector('.belt-builder__footer-actions');
    if (panelContent) panelContent.innerHTML = this.renderStepContent();
    if (priceEl) priceEl.textContent = this.formatMoney(this.getTotalPriceCents());
    if (footerActions) {
      footerActions.innerHTML = this.renderContinueButton();
      this.updateFooterCtaInPlace(footerActions);
    }

    const baseImg = this.mount.querySelector('.belt-builder__preview-img--base');
    if (baseImg) {
      const builderBase = this.builderBasePreviewPng;
      const sizePreview = this.builderSizePreviewPng;
      const src = sizePreview || builderBase;
      if (src) {
        baseImg.src = src;
        this.ui.activeMainImageSrc = src;
      }
    }
  }

  /**
   * For select-pouch-option only: updates selected classes in the clicked option group,
   * price, footer CTA, and preview overlays. No innerHTML replacement = no flicker.
   */
  updatePouchOptionSelectionOnly(clickedBtn) {
    const optionsContainer = clickedBtn?.closest('[data-option-group]') || clickedBtn?.closest('.belt-builder__options--compact');
    const tiles = optionsContainer ? optionsContainer.querySelectorAll('[data-action="select-pouch-option"]') : [];
    if (typeof window !== 'undefined' && console?.log) {
      console.log('[BB] updatePouchOptionSelectionOnly', { fn: 'updatePouchOptionSelectionOnly', stage: this.ui.stage, reason: this._lastAction || 'unknown', groupId: optionsContainer?.getAttribute?.('data-option-group'), tileCount: tiles.length, willReplacePanel: false });
    }
    if (optionsContainer) {
      tiles.forEach((btn) => {
        const isSelected = btn === clickedBtn;
        btn.classList.toggle('belt-builder__option--selected', isSelected);
        btn.classList.toggle('belt-builder__card--selected', isSelected);
      });
    }
    const priceEl = this.mount.querySelector('.belt-builder__price-value');
    const footerActions = this.mount.querySelector('.belt-builder__footer-actions');
    if (priceEl) priceEl.textContent = this.formatMoney(this.getTotalPriceCents());
    this.updateFooterCtaInPlace(footerActions);
    this.updatePreviewOnly();
  }

  /**
   * Updates only the left panel content, price, footer, and preview.
   * Used for select-pouch (setup panel changes). Thumbnails depend on selected pouch product.
   */
  updatePouchStepOnly() {
    const panelContent = this.mount.querySelector('.belt-builder__panel-content');
    const previewArea = this.mount.querySelector('.belt-builder__preview-area');
    const footerActions = this.mount.querySelector('.belt-builder__footer-actions');
    if (typeof window !== 'undefined' && console?.log) {
      console.log('[BB] updatePouchStepOnly', { fn: 'updatePouchStepOnly', stage: this.ui.stage, reason: this._lastAction || 'unknown', willReplacePanel: !!panelContent, willReplacePreview: !!previewArea, willReplaceFooter: !!footerActions });
    }
    const priceEl = this.mount.querySelector('.belt-builder__price-value');
    if (panelContent) panelContent.innerHTML = this.renderStepContent();
    if (priceEl) priceEl.textContent = this.formatMoney(this.getTotalPriceCents());
    if (footerActions) {
      footerActions.innerHTML = this.renderContinueButton();
      this.updateFooterCtaInPlace(footerActions);
    }
    if (previewArea) previewArea.innerHTML = this.renderPreview();
  }

  setupMobileFooterObserver() {
    if (!window.matchMedia('(max-width: 1024px)').matches) return;

    const sentinel = this.mount.querySelector('.belt-builder__footer-sentinel');
    const footer = this.mount.querySelector('.belt-builder__left-panel-footer');
    if (!sentinel || !footer) return;

    const footerHeight = footer.getBoundingClientRect().height;
    const safeFallback = 120;
    const measuredHeight = Number.isFinite(footerHeight) && footerHeight > 0 ? Math.round(footerHeight) : safeFallback;
    this.mount.style.setProperty('--belt-builder-footer-height', `${measuredHeight}px`);

    const rootMarginBottom = -measuredHeight;
    const rootMargin = `0px 0px ${rootMarginBottom}px 0px`;

    this._footerObserver = new IntersectionObserver(
      ([entry]) => {
        const atEnd = entry.isIntersecting;
        footer.classList.toggle('belt-builder__footer--fixed', !atEnd);
        footer.classList.toggle('belt-builder__footer--static', atEnd);
      },
      { root: null, rootMargin, threshold: 0 }
    );

    footer.classList.add('belt-builder__footer--fixed');
    this._footerObserver.observe(sentinel);
  }

  teardownMobileFooterObserver() {
    if (this._footerObserver) {
      this._footerObserver.disconnect();
      this._footerObserver = null;
    }
    const footer = this.mount?.querySelector('.belt-builder__left-panel-footer');
    if (footer) {
      footer.classList.remove('belt-builder__footer--fixed', 'belt-builder__footer--static');
    }
    if (this.mount) {
      this.mount.style.removeProperty('--belt-builder-footer-height');
    }
  }

  renderBreadcrumbs() {
    const idx = STEPS.indexOf(this.ui.stage);
    return STEPS.map((step, i) => {
      const isActive = step === this.ui.stage;
      const isPast = i < idx;
      const canNavigate = this.canNavigateToStep(step);
      const isLocked = !canNavigate;
      const lockedClass = isLocked ? 'belt-builder__crumb--locked' : '';
      return `
        <button type="button" class="belt-builder__crumb ${isActive ? 'belt-builder__crumb--active' : ''} ${isPast ? 'belt-builder__crumb--past' : ''} ${lockedClass}"
          data-action="go-step" data-step="${step}" ${isLocked ? 'disabled' : ''}>
          ${STEP_LABELS[step]}
        </button>
      `;
    }).join('');
  }

  renderStepContent() {
    if (typeof window !== 'undefined' && console?.log) {
      console.log('[BB] renderStepContent', { fn: 'renderStepContent', stage: this.ui.stage, reason: this._lastAction || 'unknown', willReplacePanel: true });
    }
    switch (this.ui.stage) {
      case 'belt': return this.renderStepBelt();
      case 'pouch': return this.renderStepPouch();
      case 'utility': return this.renderStepUtility();
      case 'loops': return this.renderStepLoops();
      case 'fittings': return this.renderStepFittings();
      default: return '';
    }
  }

  renderStepBelt() {
    const { orientation, size } = this.state.baseBelt;
    const variant = this.findVariant(orientation, size);

    return `
      <div class="belt-builder__step-header">
        <h2 class="belt-builder__step-heading">T1 Belt System</h2>
        <p class="belt-builder__step-desc">TruGear belt is core of the work system, you can choose from different sizes and direction of the belt.</p>
      </div>
      <div class="belt-builder__step">
        <h3 class="belt-builder__step-title">Orientation</h3>
        <div class="belt-builder__options belt-builder__options--orientation">
          <button class="belt-builder__option ${orientation === 'LEFT HANDED' ? 'belt-builder__option--selected' : ''}"
            data-choice="orientation" data-value="LEFT HANDED" type="button">Left Handed</button>
          <button class="belt-builder__option ${orientation === 'RIGHT HANDED' ? 'belt-builder__option--selected' : ''}"
            data-choice="orientation" data-value="RIGHT HANDED" type="button">Right Handed</button>
        </div>
      </div>
      <div class="belt-builder__step">
        <div class="belt-builder__step-title-row">
          <h3 class="belt-builder__step-title">Size</h3>
          <button type="button" class="belt-builder__link-action" data-action="open-measure-drawer">How to measure</button>
        </div>
        <div class="belt-builder__options">
          ${this.renderSizeOptions(size, orientation)}
        </div>
      </div>
    `;
  }

  renderSizeOptions(selectedSize, orientation) {
    const sizeValues = this.allBeltSizeValues;
    const sizes = sizeValues.map((value) => ({ value, label: value }));
    return sizes.map(({ value, label }) => {
      const isSelected = selectedSize === value;
      return `
        <button type="button" class="belt-builder__option ${isSelected ? 'belt-builder__option--selected' : ''}"
          data-choice="size" data-value="${String(value).replace(/"/g, '&quot;')}">${label}</button>
      `;
    }).join('');
  }

  renderPouchOptionSelector(stateKey, optionName, values, product) {
    if (!values?.length) return '';
    const sel = this.state[stateKey];
    const stateField = this.optionNameToStateKey(optionName);
    const selected = sel?.[stateField];
    const orientation = stateKey === 'primaryPouch' ? this.primaryOrientation : this.secondaryOrientation;
    const valueToVariantId = {};
    if (BB_DEBUG_AVAILABILITY && typeof window !== 'undefined' && window.console?.log) {
      console.log('[BB] renderPouchOptionSelector', { productTitle: product?.title, productId: product?.productId, stateKey, optionName, values, currentSelections: sel ? { ...sel } : null });
    }
    const tilesHtml = values.map((val) => {
      const isOutOfStock = product ? !this.isOptionValueAvailable(product, stateKey, optionName, val) : false;
      const isSelected = selected != null && String(val).trim() === String(selected).trim();
      const result = product ? this.getOptionCardVariantImage(product, orientation, optionName, val, stateKey) : null;
      const thumbnailUrl = result?.image ?? null;
      if (result?.variantId != null) valueToVariantId[val] = result.variantId;
      const disabledClass = isOutOfStock ? ' belt-builder__card--disabled' : '';
      const stockLabel = isOutOfStock ? '<div class="belt-builder__stock-label">OUT OF STOCK</div>' : '';
      if (BB_DEBUG_AVAILABILITY && typeof window !== 'undefined' && window.console?.log) {
        console.log('[BB] renderPouchOptionSelector tile', { optionName, value: val, isOutOfStock, isSelected, willRenderDisabled: isOutOfStock });
      }
      return `
        <button type="button" class="belt-builder__option belt-builder__card${disabledClass} ${isSelected ? 'belt-builder__option--selected belt-builder__card--selected' : ''}"
          data-action="select-pouch-option" data-state-key="${stateKey}" data-option-name="${String(optionName).replace(/"/g, '&quot;')}" data-value="${String(val).replace(/"/g, '&quot;')}"${isOutOfStock ? ' disabled' : ''}>
          <span class="belt-builder__option-card-wrap" style="position:relative;display:block;">
            ${thumbnailUrl ? `<span class="belt-builder__option-card-thumb"><img src="${thumbnailUrl}" alt="${String(val).replace(/"/g, '&quot;')}" class="belt-builder__option-card-img"></span>` : ''}
            ${stockLabel}
            <span class="belt-builder__option-card-label">${val}</span>
          </span>
        </button>
      `;
    }).join('');
    this.addDuplicateVariantWarning(optionName, values, valueToVariantId);
    const groupId = `opt-${stateKey}-${String(optionName).replace(/\s+/g, '-')}`;
    return `
      <div class="belt-builder__step">
        <h3 class="belt-builder__step-title">${optionName}</h3>
        <div class="belt-builder__options belt-builder__options--cards belt-builder__options--compact" data-option-group="${groupId}">
          ${tilesHtml}
        </div>
      </div>
    `;
  }

  renderPouchSetupPanel(stateKey) {
    const sel = this.state[stateKey];
    if (!sel?.productId) return '';
    const productList = stateKey === 'primaryPouch' ? this.products.primaryPouches : this.products.secondaryPouches;
    const product = (productList || []).find((p) => String(p.productId) === String(sel.productId));
    if (!product) return '';

    const allowed = POUCH_OPTION_WHITELIST[stateKey === 'primaryPouch' ? 'primary' : 'secondary'];
    const sections = [];
    for (const optName of allowed) {
      const pos = this.getOptionPosition(product, optName);
      if (!pos) continue;
      const values = this.getVisibleOptionValues(product, optName);
      if (values.length === 0) {
        return `<div class="belt-builder__pouch-setup"></div>`;
      }
      sections.push(this.renderPouchOptionSelector(stateKey, optName, values, product));
    }
    if (sections.length === 0) return '';

    return `
      <div class="belt-builder__pouch-setup">
        <h3 class="belt-builder__step-title">Pouch setup</h3>
        ${sections.join('')}
      </div>
    `;
  }

  renderStepPouch() {
    const sideLabel = this.primarySide === 'RH' ? 'Right' : 'Left';
    const pouches = this.products.primaryPouches || [];

    return `
      <div class="belt-builder__step-header">
        <h2 class="belt-builder__step-heading">${sideLabel} Side Pouch</h2>
        <p class="belt-builder__step-desc">Your primary pouch sits on your dominant side for fast, natural access. This is where the tools you use most should live.</p>
      </div>
      <div class="belt-builder__options belt-builder__options--cards">
        ${pouches.map(p => this.renderPouchCard(p, this.state.primaryPouch?.productId ?? null, 'primaryPouch')).join('')}
      </div>
      ${this.renderPouchSetupPanel('primaryPouch')}
    `;
  }

  renderStepUtility() {
    const sideLabel = this.secondarySide === 'RH' ? 'Right' : 'Left';
    const pouches = this.products.secondaryPouches || [];

    return `
      <div class="belt-builder__step-header">
        <h2 class="belt-builder__step-heading">${sideLabel} Side Pouch</h2>
        <p class="belt-builder__step-desc">The secondary pouch supports your main workflow. Use it to balance your load and carry tools you reach for less often.</p>
      </div>
      <div class="belt-builder__options belt-builder__options--cards">
        ${pouches.map(p => this.renderPouchCard(p, this.state.secondaryPouch?.productId ?? null, 'secondaryPouch')).join('')}
      </div>
      ${this.renderPouchSetupPanel('secondaryPouch')}
    `;
  }

  renderPouchCard(pouch, selectedProductId, stateKey) {
    const orientation = stateKey === 'primaryPouch' ? this.primaryOrientation : this.secondaryOrientation;
    const isSelected = selectedProductId != null && String(selectedProductId) === String(pouch.productId);
    const isOutOfStock = this.isPouchOutOfStockForOrientation(pouch, orientation);
    const selections = isSelected && this.state[stateKey]
      ? this.getPouchSelectionsForProduct(stateKey, pouch)
      : { Orientation: orientation };
    const variant = this.resolveVariantBySelections(pouch, selections);
    const priceCents = variant?.priceCents ?? 0;
    const displayImage = pouch.image || pouch.images?.[0] || '';
    const disabledClass = isOutOfStock ? 'belt-builder__card--disabled' : '';
    const stockLabel = isOutOfStock ? '<div class="belt-builder__stock-label">OUT OF STOCK</div>' : '';
    const disabledAttr = isOutOfStock ? ' disabled' : '';
    const ariaDisabled = isOutOfStock ? ' aria-disabled="true"' : '';
    return `
      <button type="button" class="belt-builder__card ${isSelected ? 'belt-builder__card--selected' : ''} ${disabledClass}"
        data-action="select-pouch" data-state-key="${stateKey}" data-product-id="${pouch.productId}"${disabledAttr}${ariaDisabled}>
        <span class="belt-builder__card-image-wrap">
          <img src="${displayImage || ''}" alt="${pouch.title}" class="belt-builder__card-image">
          ${stockLabel}
          <span class="belt-builder__card-info-icon" data-action="open-info" data-product-id="${pouch.productId}" data-product-type="pouch" role="button" tabindex="0" aria-label="Product details">i</span>
        </span>
        <span class="belt-builder__card-title">${pouch.title}</span>
        <span class="belt-builder__card-price">${this.formatMoney(priceCents)}</span>
      </button>
    `;
  }

  get loopsForDisplay() {
    return (this.products.loops || []).map((p) => {
      const v = p.variants?.[0];
      return {
        productId: p.productId,
        variantId: v?.variantId ?? v?.id,
        title: p.title,
        priceCents: v?.priceCents ?? 0,
        image: p.image
      };
    }).filter((l) => l.variantId);
  }

  get fittingsForDisplay() {
    return (this.products.accessories || []).map((p) => {
      const v = p.variants?.[0];
      return {
        productId: p.productId,
        variantId: v?.variantId ?? v?.id,
        title: p.title,
        priceCents: v?.priceCents ?? 0,
        image: p.image
      };
    }).filter((f) => f.variantId);
  }

  renderStepLoops() {
    const products = (this.products.loops || []).filter((p) => p.variants?.[0]);

    return `
      <div class="belt-builder__step-header">
        <h2 class="belt-builder__step-heading">Loops</h2>
        <p class="belt-builder__step-desc">Add as many or as few loops as you need — there's no fixed setup. You can always change your setup as your tools change.</p>
      </div>
      <div class="belt-builder__options belt-builder__options--grid">
        ${products.map((p) => this.renderLoopCard(p)).join('')}
      </div>
    `;
  }

  renderLoopCard(product) {
    const v = product.variants?.[0];
    const variantId = v?.variantId ?? v?.id;
    const count = this.state.loops.filter((l) => String(l.variantId ?? l.productId) === String(variantId)).length;
    const isSelected = count > 0;
    const isOutOfStock = this.isProductOutOfStock(product);
    const disabledClass = isOutOfStock ? 'belt-builder__card--disabled' : '';
    const stockLabel = isOutOfStock ? '<div class="belt-builder__stock-label">OUT OF STOCK</div>' : '';
    const ariaDisabled = isOutOfStock ? ' aria-disabled="true"' : '';
    return `
      <button type="button" class="belt-builder__card belt-builder__card--loop ${isSelected ? 'belt-builder__card--selected' : ''} ${disabledClass}"
        data-action="toggle-loop" data-product-id="${product.productId}"${ariaDisabled}>
        <span class="belt-builder__card-image-wrap">
          <img src="${product.image || ''}" alt="${product.title}" class="belt-builder__card-image">
          ${stockLabel}
          <span class="belt-builder__card-info-icon" data-action="open-info" data-product-id="${product.productId}" data-product-type="loop" role="button" tabindex="0" aria-label="Product details">i</span>
        </span>
        <span class="belt-builder__card-title">${product.title}</span>
        <span class="belt-builder__card-price">${this.formatMoney(v?.priceCents ?? 0)}</span>
      </button>
    `;
  }

  renderStepFittings() {
    const products = (this.products.accessories || []).filter((p) => p.variants?.[0]);

    return `
      <div class="belt-builder__step-header">
        <h2 class="belt-builder__step-heading">Fittings & Support</h2>
        <p class="belt-builder__step-desc">Add fittings to improve comfort and stability. Suspenders and padding help distribute weight and reduce fatigue during long days on the job.</p>
      </div>
      <div class="belt-builder__options belt-builder__options--grid">
        ${products.map((p) => this.renderFittingCard(p)).join('')}
      </div>
    `;
  }

  renderFittingCard(product) {
    const v = product.variants?.[0];
    const variantId = v?.variantId ?? v?.id;
    const count = this.state.fittings.filter((f) => String(f.variantId ?? f.productId) === String(variantId)).length;
    const isSelected = count > 0;
    const isOutOfStock = this.isProductOutOfStock(product);
    const disabledClass = isOutOfStock ? 'belt-builder__card--disabled' : '';
    const stockLabel = isOutOfStock ? '<div class="belt-builder__stock-label">OUT OF STOCK</div>' : '';
    const ariaDisabled = isOutOfStock ? ' aria-disabled="true"' : '';
    return `
      <button type="button" class="belt-builder__card belt-builder__card--fitting ${isSelected ? 'belt-builder__card--selected' : ''} ${disabledClass}"
        data-action="toggle-fitting" data-product-id="${product.productId}"${ariaDisabled}>
        <span class="belt-builder__card-image-wrap">
          <img src="${product.image || ''}" alt="${product.title}" class="belt-builder__card-image">
          ${stockLabel}
          <span class="belt-builder__card-info-icon" data-action="open-info" data-product-id="${product.productId}" data-product-type="fitting" role="button" tabindex="0" aria-label="Product details">i</span>
        </span>
        <span class="belt-builder__card-title">${product.title}</span>
        <span class="belt-builder__card-price">${this.formatMoney(v?.priceCents ?? 0)}</span>
      </button>
    `;
  }

  renderContinueButton() {
    const arrow = '<span class="belt-builder__continue-arrow" aria-hidden="true"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 5L5 12L12 19M5 12L19 12" stroke="#E1752D" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" transform="translate(12,12) scale(-1,1) translate(-12,-12)"/></svg></span>';
    if (this.ui.stage === 'fittings') {
      const items = this.buildCartItemsFromState();
      const canAdd = items.length > 0;
      const cartErrorHtml = this.ui.cartError
        ? `<p class="belt-builder__cart-error" role="alert">${this.ui.cartError}</p>`
        : '';
      return `
        ${cartErrorHtml}
        <button type="button" class="belt-builder__continue belt-builder__add-to-cart" data-action="add-setup-to-cart"
          ${canAdd ? '' : 'disabled'}>Add setup to cart ${arrow}</button>
      `;
    }

    const canProceed = this.canProceedToNextStage();
    const showHint = this.ui.showStepHint && (this.ui.stage === 'pouch' || this.ui.stage === 'utility');
    const hintMessage = this.ui.stage === 'pouch' ? 'Please complete pouch setup to continue' : 'Please complete utility pouch setup to continue';
    const hintHtml = showHint
      ? `<div class="belt-builder__footer-hint">${hintMessage}</div>`
      : '<div class="belt-builder__footer-hint" style="display:none" aria-hidden="true"></div>';
    return `
      ${hintHtml}
      <div class="belt-builder__continue-wrap">
        <button type="button" class="belt-builder__continue" data-action="next-stage"
          ${canProceed ? '' : 'disabled'}>CONTINUE ${arrow}</button>
        <div class="belt-builder__continue-overlay" aria-hidden="true"></div>
      </div>
    `;
  }

  get builderBasePreviewPng() {
    const baseVariant = this.getBaseVariant();
    const selectedBelt = this.getSelectedBeltProduct();
    return (
      selectedBelt?.builderBaseImage ||
      baseVariant?.image ||
      selectedBelt?.image ||
      this.baseBeltImageUrl ||
      this.firstBeltProductImage
    );
  }

  renderPreview() {
    const baseVariant = this.getBaseVariant();
    const thumbImages = this.getThumbnailImagesForStep();
    const builderBase = this.builderBasePreviewPng;
    const sizePreview = this.builderSizePreviewPng;

    if (!builderBase && this.ui.stage !== 'belt') {
      return '<div class="belt-builder__preview-placeholder"><p>Select a base belt to see preview</p></div>';
    }

    if (this.ui.stage === 'belt') {
      const beltStageImage = sizePreview || builderBase;
      if (!beltStageImage) {
        return '<div class="belt-builder__preview-placeholder"><p>Select a base belt to see preview</p></div>';
      }
      if (!this.ui.activeMainImageSrc || this.ui.activeMainImageSrc === builderBase) {
        this.ui.activeMainImageSrc = beltStageImage;
      }
      if (thumbImages.length && !thumbImages.includes(this.ui.activeMainImageSrc)) {
        this.ui.activeMainImageSrc = beltStageImage;
        this.ui.activeThumbIndex = 0;
      }
    } else {
      if (!this.ui.activeMainImageSrc && thumbImages.length) {
        this.ui.activeMainImageSrc = thumbImages[0];
        this.ui.activeThumbIndex = 0;
      }
      if (thumbImages.length && !thumbImages.includes(this.ui.activeMainImageSrc)) {
        this.ui.activeMainImageSrc = thumbImages[0];
        this.ui.activeThumbIndex = 0;
      }
    }

    const activeSrc = this.ui.activeMainImageSrc || builderBase || thumbImages[0];
    if (!activeSrc) {
      return '<div class="belt-builder__preview-placeholder"><p>Select a base belt to see preview</p></div>';
    }

    const dominantRight = this.dominantSide === 'RH';
    const primarySideClass = dominantRight ? 'belt-builder__preview-img--right' : 'belt-builder__preview-img--left';
    const secondarySideClass = dominantRight ? 'belt-builder__preview-img--left' : 'belt-builder__preview-img--right';

    const showOverlays = activeSrc === builderBase && this.ui.stage !== 'belt';
    const primaryOverlaySrc = this.state.primaryPouch?.overlayImage || '';
    const secondaryOverlaySrc = this.state.secondaryPouch?.overlayImage || '';

    const previewContent = showOverlays
      ? `
        <img src="${activeSrc}" alt="Belt" class="belt-builder__preview-img belt-builder__preview-img--base">
        ${primaryOverlaySrc ? `<img src="${primaryOverlaySrc}" alt="${this.state.primaryPouch?.title || ''}" class="belt-builder__preview-img belt-builder__preview-img--primary ${primarySideClass}">` : `<img src="" alt="" class="belt-builder__preview-img belt-builder__preview-img--primary ${primarySideClass}" style="display:none">`}
        ${secondaryOverlaySrc ? `<img src="${secondaryOverlaySrc}" alt="${this.state.secondaryPouch?.title || ''}" class="belt-builder__preview-img belt-builder__preview-img--secondary ${secondarySideClass}">` : `<img src="" alt="" class="belt-builder__preview-img belt-builder__preview-img--secondary ${secondarySideClass}" style="display:none">`}
      `
      : `<img src="${activeSrc}" alt="Preview" class="belt-builder__preview-img belt-builder__preview-img--base">`;

    const hasMultipleImages = thumbImages.length > 1;

    return `
      <div class="belt-builder__preview-wrap">
        <div class="belt-builder__preview-stack belt-builder__preview-stack--system">
          <div class="belt-builder__preview-stage">
            ${previewContent}
          </div>
        </div>
        ${hasMultipleImages ? `
          <button type="button" class="belt-builder__preview-nav belt-builder__preview-nav--prev" data-action="prev-image" aria-label="Previous image">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <button type="button" class="belt-builder__preview-nav belt-builder__preview-nav--next" data-action="next-image" aria-label="Next image">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
        ` : ''}
      </div>
      <div class="belt-builder__preview-thumbs">
        ${thumbImages.slice(0, 8).map((img, i) => {
          const isActive = this.ui.activeThumbIndex === i;
          const thumbClass = `belt-builder__thumb ${isActive ? 'belt-builder__thumb--active' : ''}`;
          return `<button type="button" class="${thumbClass}" data-action="select-thumb" data-src="${String(img).replace(/&/g, '&amp;').replace(/"/g, '&quot;')}" data-index="${i}"><img src="${img}" alt=""></button>`;
        }).join('')}
        ${thumbImages.length > 8 ? `<span class="belt-builder__thumb-more">+${thumbImages.length - 8}</span>` : ''}
      </div>
    `;
  }

  getInfoProductById(productId, productType) {
    if (!productId) return null;
    const id = String(productId);
    if (productType === 'pouch') {
      const product = [...(this.products.primaryPouches || []), ...(this.products.secondaryPouches || [])].find((p) => String(p.productId) === id);
      return product ? { title: product.title, images: product.images || [], descriptionHtml: product.descriptionHtml || '' } : null;
    }
    if (productType === 'loop') {
      const product = (this.products.loops || []).find((p) => String(p.productId) === id);
      return product ? { title: product.title, images: product.images || [], descriptionHtml: product.descriptionHtml || '' } : null;
    }
    if (productType === 'fitting') {
      const product = (this.products.accessories || []).find((p) => String(p.productId) === id);
      return product ? { title: product.title, images: product.images || [], descriptionHtml: product.descriptionHtml || '' } : null;
    }
    if (productType === 'belt') {
      const product = (this.products.belts || []).find((p) => String(p.productId) === id);
      return product ? { title: product.title, images: product.images || [], descriptionHtml: product.descriptionHtml || '' } : null;
    }
    return null;
  }

  renderInfoOverlayHTML() {
    if (!this.ui.infoProduct) return '';

    const { title, images, descriptionHtml } = this.ui.infoProduct;
    const activeIdx = this.ui.infoActiveImageIndex ?? 0;
    const mainImg = images[activeIdx] || images[0] || '';

    return `
      <div class="belt-builder__info-overlay" role="dialog" aria-modal="true" aria-label="Product details">
        <div class="belt-builder__info-backdrop" data-action="close-info-backdrop"></div>
        <div class="belt-builder__info-drawer">
          <button type="button" class="belt-builder__info-close" data-action="close-info" aria-label="Close">×</button>
          <div class="belt-builder__info-scroll">
            <div class="belt-builder__info-gallery">
              <div class="belt-builder__info-main-img">
                <img src="${mainImg}" alt="${title}">
              </div>
              <div class="belt-builder__info-thumbs">
                ${(images || []).slice(0, 8).map((img, i) => {
                  const isActive = i === activeIdx;
                  return `<button type="button" class="belt-builder__info-thumb ${isActive ? 'belt-builder__info-thumb--active' : ''}" data-action="info-thumb" data-index="${i}"><img src="${img}" alt=""></button>`;
                }).join('')}
              </div>
            </div>
            <div class="belt-builder__info-body">
              <h3 class="belt-builder__info-title">${title}</h3>
              <div class="belt-builder__info-description">${descriptionHtml || ''}</div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  renderMeasureDrawerHTML() {
    const base = (typeof window !== 'undefined' && window.Shopify?.routes?.root) ? window.Shopify.routes.root.replace(/\/$/, '') : '';
    const sizingGuideUrl = base ? `${base}/pages/sizing-guide` : '/pages/sizing-guide';

    const measureImages = (typeof window !== 'undefined' && window.BELT_BUILDER_MEASURE_IMAGES) ? window.BELT_BUILDER_MEASURE_IMAGES : {};
    const bodyImageUrl = measureImages.measureBodyImage || null;
    const beltImageUrl = measureImages.measureBeltImage || null;

    const bodyImageHtml = bodyImageUrl
      ? `<div class="belt-builder__measure-image-placeholder"><img src="${String(bodyImageUrl).replace(/"/g, '&quot;')}" alt="Measure your body" class="belt-builder__measure-img" loading="lazy" width="800" height="500"></div>`
      : '<div class="belt-builder__measure-image-placeholder" aria-hidden="true"></div>';
    const beltImageHtml = beltImageUrl
      ? `<div class="belt-builder__measure-image-placeholder"><img src="${String(beltImageUrl).replace(/"/g, '&quot;')}" alt="Measure your current belt" class="belt-builder__measure-img" loading="lazy" width="800" height="500"></div>`
      : '<div class="belt-builder__measure-image-placeholder" aria-hidden="true"></div>';

    return `
      <div class="belt-builder__info-overlay belt-builder__measure-overlay" role="dialog" aria-modal="true" aria-label="How to measure">
        <div class="belt-builder__info-backdrop" data-action="close-measure-backdrop"></div>
        <div class="belt-builder__info-drawer belt-builder__measure-drawer">
          <div class="belt-builder__measure-drawer-header">
            <h2 class="belt-builder__measure-drawer-title">How to measure</h2>
            <button type="button" class="belt-builder__info-close" data-action="close-measure-drawer" aria-label="Close">×</button>
          </div>
          <div class="belt-builder__measure-drawer-content">
            <div class="belt-builder__measure-body">
              <p class="belt-builder__measure-intro">Measure where the tool belt will sit (usually slightly below the waist). Use this measurement to choose the correct size.</p>

              <section class="belt-builder__measure-section">
                <h4 class="belt-builder__measure-section-title">Measure your body</h4>
                ${bodyImageHtml}
                <ol class="belt-builder__measure-steps">
                  <li>Wrap a measuring tape around your hips where the belt will sit.</li>
                  <li>Keep the tape level and snug, but not tight.</li>
                  <li>Use this measurement to choose your belt size.</li>
                </ol>
              </section>

              <section class="belt-builder__measure-section">
                <h4 class="belt-builder__measure-section-title">Measure your current belt</h4>
                ${beltImageHtml}
                <ol class="belt-builder__measure-steps">
                  <li>Lay your belt flat on a table.</li>
                  <li>Measure the padded section only.</li>
                  <li>Match the measurement with the size chart.</li>
                </ol>
              </section>

              <section class="belt-builder__measure-section">
                <h4 class="belt-builder__measure-section-title">Size chart</h4>
                <div class="belt-builder__measure-table-wrap">
                  <table class="belt-builder__measure-table">
                    <thead>
                      <tr>
                        <th scope="col">SIZE</th>
                        <th scope="col">INCHES</th>
                        <th scope="col">CM</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr><td>XS</td><td>25"</td><td>65 cm</td></tr>
                      <tr><td>S</td><td>29"</td><td>75 cm</td></tr>
                      <tr><td>M</td><td>33"</td><td>85 cm</td></tr>
                      <tr><td>L</td><td>37"</td><td>95 cm</td></tr>
                      <tr><td>XL</td><td>41"</td><td>105 cm</td></tr>
                    </tbody>
                  </table>
                </div>
                <p class="belt-builder__measure-note">Sizes refer to the padded belt section. If you regularly work with heavy winter clothing, consider choosing the larger size.</p>
              </section>

              <div class="belt-builder__measure-footer">
                <a href="${sizingGuideUrl}" class="belt-builder__measure-link">View full sizing guide →</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  renderMeasureDrawerToBody() {
    this.cleanupMeasureOverlay();
    if (!this.ui.isMeasureDrawerOpen) return;

    document.documentElement.classList.add('belt-builder-overlay-open');
    document.body.classList.add('belt-builder-overlay-open');

    const root = document.createElement('div');
    root.id = 'belt-builder-measure-overlay-root';
    root.className = 'belt-builder-overlay-root';
    root.innerHTML = this.renderMeasureDrawerHTML();
    document.body.appendChild(root);

    root.addEventListener('click', (e) => {
      const actionBtn = e.target.closest('[data-action]');
      if (!actionBtn || !root.contains(actionBtn)) return;
      const action = actionBtn.dataset.action;
      if (action === 'close-measure-drawer' || action === 'close-measure-backdrop') {
        e.preventDefault();
        e.stopPropagation();
        this.ui.isMeasureDrawerOpen = false;
        this.cleanupMeasureOverlay();
        this.removeMeasureDrawerListeners();
      }
    });

    this._measureKeyHandler = (e) => {
      if (e.key === 'Escape' && this.ui.isMeasureDrawerOpen) {
        this.ui.isMeasureDrawerOpen = false;
        this.cleanupMeasureOverlay();
        this.removeMeasureDrawerListeners();
      }
    };
    document.addEventListener('keydown', this._measureKeyHandler);
  }

  cleanupMeasureOverlay() {
    const root = document.getElementById('belt-builder-measure-overlay-root');
    if (root) root.remove();
    const infoRoot = document.getElementById('belt-builder-overlay-root');
    if (!infoRoot) {
      document.documentElement.classList.remove('belt-builder-overlay-open');
      document.body.classList.remove('belt-builder-overlay-open');
    }
  }

  removeMeasureDrawerListeners() {
    if (this._measureKeyHandler) {
      document.removeEventListener('keydown', this._measureKeyHandler);
      this._measureKeyHandler = null;
    }
  }

  renderInfoOverlayToBody() {
    this.cleanupOverlay();
    if (!this.ui.isInfoOpen || !this.ui.infoProduct) return;

    document.documentElement.classList.add('belt-builder-overlay-open');
    document.body.classList.add('belt-builder-overlay-open');

    const root = document.createElement('div');
    root.id = 'belt-builder-overlay-root';
    root.className = 'belt-builder-overlay-root';
    root.innerHTML = this.renderInfoOverlayHTML();
    document.body.appendChild(root);

    root.addEventListener('click', (e) => {
      const actionBtn = e.target.closest('[data-action]');
      if (!actionBtn || !root.contains(actionBtn)) return;

      e.preventDefault();
      e.stopPropagation();
      const action = actionBtn.dataset.action;

      if (action === 'close-info' || action === 'close-info-backdrop') {
        this.ui.isInfoOpen = false;
        this.ui.infoProduct = null;
        this.cleanupOverlay();
        this.removeInfoOverlayListeners();
        return;
      }

      if (action === 'info-thumb') {
        const index = parseInt(actionBtn.dataset.index, 10);
        if (!isNaN(index) && this.ui.infoProduct) {
          this.ui.infoActiveImageIndex = index;
          this.updateInfoOverlayImage(root);
        }
      }
    });
  }

  updateInfoOverlayImage(root) {
    const { images } = this.ui.infoProduct || {};
    const idx = this.ui.infoActiveImageIndex ?? 0;
    const newSrc = images?.[idx] || images?.[0] || '';

    const mainImg = root?.querySelector('.belt-builder__info-main-img img');
    if (mainImg) mainImg.src = newSrc;

    const thumbs = root?.querySelectorAll('.belt-builder__info-thumb');
    thumbs?.forEach((thumb, i) => {
      thumb.classList.toggle('belt-builder__info-thumb--active', i === idx);
    });
  }

  cleanupOverlay() {
    const root = document.getElementById('belt-builder-overlay-root');
    if (root) root.remove();
    const measureRoot = document.getElementById('belt-builder-measure-overlay-root');
    if (!measureRoot) {
      document.documentElement.classList.remove('belt-builder-overlay-open');
      document.body.classList.remove('belt-builder-overlay-open');
    }
  }

  getThumbnailImagesForStep() {
    const step = this.ui.stage;
    if (step === 'belt') {
      const baseBeltImg = this.builderBasePreviewPng || this.baseBeltImageUrl;
      if (!baseBeltImg) return [];
      const productImages = (this.products.belts || []).flatMap((p) => p.images || []).filter(Boolean);
      const seen = new Set([baseBeltImg]);
      const deduped = productImages.filter((url) => {
        if (seen.has(url)) return false;
        seen.add(url);
        return true;
      });
      return [baseBeltImg, ...deduped];
    }

    const baseVariant = this.getBaseVariant();
    const baseImage = baseVariant?.image || this.firstBeltProductImage;
    if (!baseImage) return [];

    const primaryPouches = this.products.primaryPouches || [];
    const secondaryPouches = this.products.secondaryPouches || [];
    const loops = this.loopsForDisplay;
    const fittings = this.fittingsForDisplay;

    switch (step) {
      case 'pouch': {
        const selected = this.state.primaryPouch
          ? primaryPouches.find((p) => String(p.productId) === String(this.state.primaryPouch.productId))
          : null;
        const productImages = selected?.images?.length
          ? selected.images.filter(Boolean)
          : primaryPouches.slice(0, 4).map((p) => p.image).filter(Boolean);
        return [baseImage, ...productImages];
      }
      case 'utility': {
        const selected = this.state.secondaryPouch
          ? secondaryPouches.find((p) => String(p.productId) === String(this.state.secondaryPouch.productId))
          : null;
        const productImages = selected?.images?.length
          ? selected.images.filter(Boolean)
          : secondaryPouches.slice(0, 4).map((p) => p.image).filter(Boolean);
        return [baseImage, ...productImages];
      }
      case 'loops': {
        const productId = this.ui.lastClickedLoopProductId || this.state.loops[0]?.productId;
        const selected = productId
          ? (this.products.loops || []).find((p) => String(p.productId) === String(productId))
          : null;
        const productImages = selected?.images?.length
          ? selected.images.filter(Boolean)
          : loops.slice(0, 4).map((l) => l.image).filter(Boolean);
        return [baseImage, ...productImages];
      }
      case 'fittings': {
        const productId = this.ui.lastClickedFittingProductId || this.state.fittings[0]?.productId;
        const selected = productId
          ? (this.products.accessories || []).find((p) => String(p.productId) === String(productId))
          : null;
        const productImages = selected?.images?.length
          ? selected.images.filter(Boolean)
          : fittings.slice(0, 4).map((f) => f.image).filter(Boolean);
        return [baseImage, ...productImages];
      }
      default:
        return [baseImage];
    }
  }

  renderChips() {
    const chips = [
      ...this.state.loops.map(l => ({ type: 'loop', ...l })),
      ...this.state.fittings.map(f => ({ type: 'fitting', ...f }))
    ];
    if (chips.length === 0) return '';

    const maxVisible = 3;
    const visible = chips.slice(0, maxVisible);
    const remaining = chips.length - maxVisible;

    return `
      <div class="belt-builder__chips">
        ${visible.map((c) => {
          const vid = c.variantId ?? c.productId;
          return `
          <span class="belt-builder__chip" data-type="${c.type}" data-variant-id="${vid}">
            ${c.title}
            <button type="button" class="belt-builder__chip-remove" data-action="remove-chip" data-type="${c.type}" data-variant-id="${vid}" aria-label="Remove">×</button>
          </span>
        `;
        }).join('')}
        ${remaining > 0 ? `<span class="belt-builder__chip belt-builder__chip--more">+${remaining} more</span>` : ''}
      </div>
    `;
  }

  /**
   * Returns { complete, reason } for a given step. Used for CONTINUE button state and footer hint.
   * - belt: required (orientation + size / variantId)
   * - pouch: required (must select product and complete options)
   * - utility: required (same for secondary pouch)
   * - loops/fittings: optional (always complete)
   */
  getStepCompletion(step) {
    switch (step) {
      case 'belt': {
        const complete = Boolean(this.state.baseBelt.variantId);
        return {
          complete,
          reason: complete ? '' : 'Please select orientation and size to continue'
        };
      }
      case 'pouch': {
        const sel = this.state.primaryPouch;
        if (!sel || !sel.productId) {
          return { complete: false, reason: 'Please select a pouch to continue' };
        }
        if (!sel.variantId) {
          return { complete: false, reason: 'Please complete pouch configuration to continue' };
        }
        const product = (this.products.primaryPouches || []).find((p) => String(p.productId) === String(sel.productId));
        if (!product) {
          return { complete: true, reason: '' };
        }
        const allowedPrimary = POUCH_OPTION_WHITELIST.primary;
        for (const optName of allowedPrimary) {
          const pos = this.getOptionPosition(product, optName);
          const values = this.getVisibleOptionValues(product, optName);
          if (!pos || values.length === 0) continue;
          const stateKey = this.optionNameToStateKey(optName);
          const val = sel[stateKey];
          if (val == null || String(val).trim() === '') {
            return { complete: false, reason: 'Please complete pouch configuration to continue' };
          }
        }
        return { complete: true, reason: '' };
      }
      case 'utility': {
        const sel = this.state.secondaryPouch;
        if (!sel || !sel.productId) {
          return { complete: false, reason: 'Please select a utility pouch to continue' };
        }
        if (!sel.variantId) {
          return { complete: false, reason: 'Please complete utility pouch configuration to continue' };
        }
        const product = (this.products.secondaryPouches || []).find((p) => String(p.productId) === String(sel.productId));
        if (!product) {
          return { complete: true, reason: '' };
        }
        const allowedSecondary = POUCH_OPTION_WHITELIST.secondary;
        for (const optName of allowedSecondary) {
          const pos = this.getOptionPosition(product, optName);
          const values = this.getVisibleOptionValues(product, optName);
          if (!pos || values.length === 0) continue;
          const stateKey = this.optionNameToStateKey(optName);
          const val = sel[stateKey];
          if (val == null || String(val).trim() === '') {
            return { complete: false, reason: 'Please complete utility pouch configuration to continue' };
          }
        }
        return { complete: true, reason: '' };
      }
      case 'loops':
      case 'fittings':
        return { complete: true, reason: '' };
      default:
        return { complete: true, reason: '' };
    }
  }

  canProceedToNextStage() {
    const { complete } = this.getStepCompletion(this.ui.stage);
    return complete;
  }

  getBaseVariant() {
    if (!this.state.baseBelt.variantId) return null;
    const v = this.flattenedBeltVariants.find((x) => String(x.variantId) === String(this.state.baseBelt.variantId));
    return v ? { ...v, id: v.variantId } : null;
  }

  getTotalPriceCents() {
    let total = 0;
    const base = this.getBaseVariant();
    if (base) total += base.priceCents;
    else total += this.lowestBeltPriceCents;
    if (this.state.primaryPouch?.variantId) total += this.state.primaryPouch.priceCents;
    if (this.state.secondaryPouch?.variantId) total += this.state.secondaryPouch.priceCents;
    this.state.loops.forEach(l => { total += l.priceCents; });
    this.state.fittings.forEach(f => { total += f.priceCents; });
    return total;
  }

  findVariant(orientation, size) {
    if (!orientation || !size) return null;
    const v = this.flattenedBeltVariants.find((x) =>
      normalize(x.option1) === normalize(orientation) && x.option2 === size
    );
    return v ? { ...v, id: v.variantId } : null;
  }

  findBeltVariant(orientation, size) {
    return this.findVariant(orientation, size);
  }

  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    return this.moneyFormat.replace('{{amount}}', amount);
  }

  /**
   * Build cart items from builder state for POST /cart/add.js.
   * Aggregates loops/fittings by variantId into quantity.
   * Every item gets _builder_group and _builder_group_id for grouping.
   */
  buildCartItemsFromState() {
    const groupId = 't1-' + Date.now();

    const baseProps = (role) => ({
      _builder_group: 'T1 Custom Setup',
      _builder_group_id: groupId,
      ...(role && { Role: role })
    });

    const items = [];

    if (this.state.baseBelt.variantId) {
      items.push({
        id: this.state.baseBelt.variantId,
        quantity: 1,
        properties: baseProps('Belt')
      });
    }

    if (this.state.primaryPouch?.variantId) {
      items.push({
        id: this.state.primaryPouch.variantId,
        quantity: 1,
        properties: baseProps('Primary Pouch')
      });
    }

    if (this.state.secondaryPouch?.variantId) {
      items.push({
        id: this.state.secondaryPouch.variantId,
        quantity: 1,
        properties: baseProps('Secondary Pouch')
      });
    }

    const loopCounts = {};
    this.state.loops.forEach((l) => {
      const vid = l.variantId ?? l.productId;
      loopCounts[vid] = (loopCounts[vid] || 0) + 1;
    });
    Object.entries(loopCounts).forEach(([vid, qty]) => {
      items.push({ id: vid, quantity: qty, properties: baseProps('Loop') });
    });

    const fittingCounts = {};
    this.state.fittings.forEach((f) => {
      const vid = f.variantId ?? f.productId;
      fittingCounts[vid] = (fittingCounts[vid] || 0) + 1;
    });
    Object.entries(fittingCounts).forEach(([vid, qty]) => {
      items.push({ id: vid, quantity: qty, properties: baseProps('Fitting') });
    });

    return items;
  }

  /**
   * Add setup to cart via AJAX, then redirect to /cart on success.
   */
  async addSetupToCart() {
    const items = this.buildCartItemsFromState();
    if (items.length === 0) return;

    const btn = this.mount.querySelector('[data-action="add-setup-to-cart"]');
    if (btn) btn.disabled = true;

    const originalText = btn?.textContent;
    if (btn) btn.textContent = 'Adding…';

    const cartRoot = (typeof window !== 'undefined' && window.Shopify?.routes?.root) ? window.Shopify.routes.root : '/';
    const url = cartRoot.replace(/\/$/, '') + '/cart/add.js';

    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.description || data.message || 'Failed to add to cart');
      }

      this.ui.cartError = null;
      window.location.href = '/cart';
    } catch (err) {
      console.error('Belt builder cart error:', err);
      const msg = err.message || 'Unable to add setup to cart. Please try again.';
      this.ui.cartError = msg;
      const footerActions = this.mount.querySelector('.belt-builder__footer-actions');
      if (footerActions) footerActions.innerHTML = this.renderContinueButton();
    }
  }

  attachInfoOverlayListeners() {
    this._infoKeyHandler = (e) => {
      if (e.key === 'Escape' && this.ui.isInfoOpen) {
        this.ui.isInfoOpen = false;
        this.ui.infoProduct = null;
        this.cleanupOverlay();
        this.removeInfoOverlayListeners();
      }
    };
    document.addEventListener('keydown', this._infoKeyHandler);
  }

  removeInfoOverlayListeners() {
    if (this._infoKeyHandler) {
      document.removeEventListener('keydown', this._infoKeyHandler);
      this._infoKeyHandler = null;
    }
  }

  attachEventListeners() {
    if (this._clickHandler) this.mount.removeEventListener('click', this._clickHandler);
    if (this._pointerdownHandler) this.mount.removeEventListener('pointerdown', this._pointerdownHandler);
    if (this._wheelHandler) this.mount.removeEventListener('wheel', this._wheelHandler, { passive: false });
    if (this._footerResizeHandler) {
      window.removeEventListener('resize', this._footerResizeHandler);
      window.removeEventListener('orientationchange', this._footerResizeHandler);
    }

    this._footerResizeHandler = () => {
      this.teardownMobileFooterObserver();
      if (this.mount && window.matchMedia('(max-width: 1024px)').matches && this.mount.querySelector('.belt-builder__left-panel-footer')) {
        this.setupMobileFooterObserver();
      }
    };
    window.addEventListener('resize', this._footerResizeHandler);
    window.addEventListener('orientationchange', this._footerResizeHandler);

    this._wheelHandler = (e) => {
      if (!this.mount.contains(e.target)) return;
      const isInsideLeftPanel = e.target.closest('.belt-builder__left-panel');
      if (isInsideLeftPanel) return;

      const scrollEl = this.mount.querySelector('.belt-builder__left-panel-scroll');
      if (!scrollEl) return;

      const { scrollTop, scrollHeight, clientHeight } = scrollEl;
      const atBottom = scrollTop + clientHeight >= scrollHeight - 1;
      const atTop = scrollTop <= 1;
      const scrollingDown = e.deltaY > 0;
      const scrollingUp = e.deltaY < 0;

      if ((atBottom && scrollingDown) || (atTop && scrollingUp)) return;

      e.preventDefault();
      scrollEl.scrollTop += e.deltaY;
    };

    this._pointerdownHandler = (e) => {
      const overlay = e.target.closest('.belt-builder__continue-overlay');
      if (overlay && overlay.style.pointerEvents === 'auto' && (this.ui.stage === 'pouch' || this.ui.stage === 'utility')) {
        e.preventDefault();
        e.stopPropagation();
        if (this._stepHintTimer) clearTimeout(this._stepHintTimer);
        this.ui.showStepHint = true;
        this._stepHintTimer = setTimeout(() => {
          this._stepHintTimer = null;
          this.ui.showStepHint = false;
          const fa = this.mount.querySelector('.belt-builder__footer-actions');
          if (fa) this.updateFooterCtaInPlace(fa);
        }, 3000);
        const footerActions = this.mount.querySelector('.belt-builder__footer-actions');
        if (footerActions) this.updateFooterCtaInPlace(footerActions);
        return;
      }
      const continueBtn = e.target.closest('.belt-builder__continue[data-action="next-stage"]');
      if (!continueBtn || !continueBtn.disabled || (this.ui.stage !== 'pouch' && this.ui.stage !== 'utility')) return;
      e.preventDefault();
      if (this._stepHintTimer) clearTimeout(this._stepHintTimer);
      this.ui.showStepHint = true;
      this._stepHintTimer = setTimeout(() => {
        this._stepHintTimer = null;
        this.ui.showStepHint = false;
        const fa = this.mount.querySelector('.belt-builder__footer-actions');
        if (fa) this.updateFooterCtaInPlace(fa);
      }, 3000);
      const footerActions = this.mount.querySelector('.belt-builder__footer-actions');
      if (footerActions) this.updateFooterCtaInPlace(footerActions);
    };

    this._clickHandler = (e) => {
      const choiceBtn = e.target.closest('button[data-choice][data-value]');
      if (choiceBtn && !choiceBtn.disabled) {
        e.preventDefault();
        const choice = choiceBtn.dataset.choice;
        const value = choiceBtn.dataset.value;
        if (choice === 'orientation') {
          this.state.baseBelt.orientation = value;
        } else if (choice === 'size') {
          this.state.baseBelt.size = value;
        }
        const variant = this.findBeltVariant(this.state.baseBelt.orientation, this.state.baseBelt.size);
        this.state.baseBelt.variantId = variant?.id || null;
        this.syncPouchVariantsOnOrientationChange();
        if (this.ui.stage === 'belt') {
          this.updateBeltStepOnly();
        } else {
          this.render();
        }
        return;
      }

      const actionBtn = e.target.closest('[data-action]');
      if (actionBtn && !actionBtn.disabled) {
        e.preventDefault();
        const action = actionBtn.dataset.action;

        if (action === 'go-step') {
          const step = actionBtn.dataset.step;
          if (!this.canNavigateToStep(step)) return;
          if (this._stepHintTimer) clearTimeout(this._stepHintTimer);
          this._stepHintTimer = null;
          this.ui.showStepHint = false;
          this.ui.stage = step;
          this.ui.isInfoOpen = false;
          this.ui.isMeasureDrawerOpen = false;
          this.cleanupOverlay();
          this.cleanupMeasureOverlay();
          this.removeMeasureDrawerListeners();
          const thumbs = this.getThumbnailImagesForStep();
          this.ui.activeMainImageSrc = thumbs[0] || null;
          this.ui.activeThumbIndex = 0;
          this.ui.cartError = null;
          this.render();
          return;
        }

        if (action === 'select-thumb') {
          const src = actionBtn.dataset.src;
          const index = parseInt(actionBtn.dataset.index, 10);
          if (src != null && !isNaN(index)) {
            this.ui.activeMainImageSrc = src;
            this.ui.activeThumbIndex = index;
            this.updatePreviewOnly();
          }
          return;
        }

        if (action === 'prev-image') {
          const thumbs = this.getThumbnailImagesForStep();
          if (thumbs.length) {
            this.ui.activeThumbIndex = this.ui.activeThumbIndex > 0 ? this.ui.activeThumbIndex - 1 : thumbs.length - 1;
            this.ui.activeMainImageSrc = thumbs[this.ui.activeThumbIndex];
            this.updatePreviewOnly();
          }
          return;
        }

        if (action === 'next-image') {
          const thumbs = this.getThumbnailImagesForStep();
          if (thumbs.length) {
            this.ui.activeThumbIndex = this.ui.activeThumbIndex < thumbs.length - 1 ? this.ui.activeThumbIndex + 1 : 0;
            this.ui.activeMainImageSrc = thumbs[this.ui.activeThumbIndex];
            this.updatePreviewOnly();
          }
          return;
        }

        if (action === 'open-info') {
          e.stopPropagation();
          const productId = actionBtn.dataset.productId;
          const productType = actionBtn.dataset.productType;
          this.ui.infoProduct = this.getInfoProductById(productId, productType);
          if (this.ui.infoProduct) {
            this.ui.isInfoOpen = true;
            this.ui.infoActiveImageIndex = 0;
            this.renderInfoOverlayToBody();
            this.attachInfoOverlayListeners();
          }
          return;
        }

        if (action === 'open-measure-drawer') {
          e.stopPropagation();
          this.ui.isMeasureDrawerOpen = true;
          this.renderMeasureDrawerToBody();
          return;
        }

        if (action === 'select-pouch') {
          this._lastAction = 'select-pouch';
          if (this._stepHintTimer) clearTimeout(this._stepHintTimer);
          this._stepHintTimer = null;
          this.ui.showStepHint = false;
          const key = actionBtn.dataset.stateKey;
          const pouches = key === 'primaryPouch' ? this.products.primaryPouches : this.products.secondaryPouches;
          const product = (pouches || []).find((p) => String(p.productId) === String(actionBtn.dataset.productId));
          const orientation = key === 'primaryPouch' ? this.primaryOrientation : this.secondaryOrientation;
          if (!product || this.isPouchOutOfStockForOrientation(product, orientation)) return;
          if (product) {
            const defaultOverlay = this.getFirstOverlayImageForProduct(product);
            this.state[key] = {
              productId: product.productId,
              title: product.title,
              variantId: null,
              overlayImage: defaultOverlay || null
            };
            if (this.ui.stage === 'pouch' || this.ui.stage === 'utility') {
              this.updatePouchStepOnly();
            } else {
              this.render();
            }
          }
          return;
        }

        if (action === 'select-pouch-option') {
          this._lastAction = 'select-pouch-option';
          if (this._stepHintTimer) clearTimeout(this._stepHintTimer);
          this._stepHintTimer = null;
          this.ui.showStepHint = false;
          const key = actionBtn.dataset.stateKey;
          const optionName = actionBtn.dataset.optionName;
          const value = actionBtn.dataset.value;
          const sel = this.state[key];
          if (!sel?.productId) return;
          const productList = key === 'primaryPouch' ? this.products.primaryPouches : this.products.secondaryPouches;
          const product = (productList || []).find((p) => String(p.productId) === String(sel.productId));
          if (!product) return;
          const stateField = this.optionNameToStateKey(optionName);
          this.state[key] = { ...sel, [stateField]: value };
          const selections = this.getPouchSelectionsForProduct(key, product);
          const variant = this.resolveVariantBySelections(product, selections);
          const variantAvailable = variant ? this.isVariantAvailable(variant) : false;
          if (BB_DEBUG_AVAILABILITY && typeof window !== 'undefined' && window.console?.log) {
            console.log('[BB] select-pouch-option', {
              productTitle: product?.title,
              key,
              optionName,
              value,
              variantId: variant?.variantId ?? null,
              variantAvailable: variant?.available,
              variantInventoryQuantity: variant?.inventory_quantity,
              variantAvailableResult: variantAvailable,
              willBlockClick: variant && !variantAvailable
            });
          }
          if (variant && !variantAvailable) {
            this.state[key] = { ...sel };
            if (BB_DEBUG_AVAILABILITY && typeof window !== 'undefined' && window.console?.log) {
              console.log('[BB] select-pouch-option BLOCKED: out-of-stock variant, state reverted');
            }
            return;
          }
          if (variant) {
            const overlayImage = this.getOverlayImageForVariant(variant);
            this.state[key] = {
              ...this.state[key],
              variantId: variant.variantId,
              priceCents: variant.priceCents,
              image: variant.image || product.image,
              overlayImage: overlayImage || null
            };
          } else {
            this.state[key] = { ...this.state[key], variantId: null, overlayImage: null };
          }
          if (this.ui.stage === 'pouch' || this.ui.stage === 'utility') {
            if (variant) {
              this.updatePouchOptionSelectionOnly(actionBtn);
            } else {
              this.updatePouchStepOnly();
            }
          } else {
            this.render();
          }
          return;
        }

        if (action === 'next-stage') {
          const idx = STEPS.indexOf(this.ui.stage);
          if (idx < STEPS.length - 1 && this.canProceedToNextStage()) {
            if (this._stepHintTimer) clearTimeout(this._stepHintTimer);
            this._stepHintTimer = null;
            this.ui.showStepHint = false;
            this.ui.stage = STEPS[idx + 1];
            this.render();
          }
          return;
        }

        if (action === 'toggle-loop') {
          e.stopPropagation();
          const productId = actionBtn.dataset.productId;
          this.ui.lastClickedLoopProductId = productId;
          const product = this.products.loops.find((p) => String(p.productId) === String(productId));
          if (product && this.isProductOutOfStock(product)) return;
          const item = this.loopsForDisplay.find((l) => String(l.productId) === String(productId));
          if (item) {
            const idx = this.state.loops.findIndex((l) => String(l.variantId) === String(item.variantId));
            if (idx >= 0) this.state.loops.splice(idx, 1);
            else this.state.loops.push({ ...item });
            this.render();
          }
          return;
        }

        if (action === 'toggle-fitting') {
          e.stopPropagation();
          const productId = actionBtn.dataset.productId;
          this.ui.lastClickedFittingProductId = productId;
          const product = this.products.accessories.find((p) => String(p.productId) === String(productId));
          if (product && this.isProductOutOfStock(product)) return;
          const item = this.fittingsForDisplay.find((f) => String(f.productId) === String(productId));
          if (item) {
            const idx = this.state.fittings.findIndex((f) => String(f.variantId) === String(item.variantId));
            if (idx >= 0) this.state.fittings.splice(idx, 1);
            else this.state.fittings.push({ ...item });
            this.render();
          }
          return;
        }

        if (action === 'remove-chip') {
          const type = actionBtn.dataset.type;
          const variantId = actionBtn.dataset.variantId;
          if (type === 'loop') {
            const idx = this.state.loops.findIndex(l => (l.variantId ?? l.productId) === variantId);
            if (idx >= 0) this.state.loops.splice(idx, 1);
          }
          if (type === 'fitting') {
            const idx = this.state.fittings.findIndex(f => (f.variantId ?? f.productId) === variantId);
            if (idx >= 0) this.state.fittings.splice(idx, 1);
          }
          this.render();
          return;
        }

        if (action === 'add-setup-to-cart') {
          this.addSetupToCart();
          return;
        }
      }
    };

    this.mount.addEventListener('click', this._clickHandler);
    this.mount.addEventListener('pointerdown', this._pointerdownHandler);
    this.mount.addEventListener('wheel', this._wheelHandler, { passive: false });
  }
}

document.addEventListener('DOMContentLoaded', async () => {
  const mount = document.getElementById('belt-builder-mount');
  if (!mount) return;

  const moneyFormat = mount.dataset.moneyFormat || '{{amount}}';

  try {
    const builder = new BeltBuilder({ mount, moneyFormat });
    if (typeof window !== 'undefined') window.__beltBuilder = builder;
    await builder.init();

    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      const b = typeof window !== 'undefined' && window.__beltBuilder;
      if (!b) return;
      b.ui.cartError = null;
      if (b.ui.stage === 'fittings') b.render();
    });
  } catch (error) {
    console.error('Failed to load belt builder:', error);
    mount.innerHTML = '<p style="color: red;">Error loading belt builder. Please check that collections builder-belts, builder-pouches, builder-loops, and builder-accessories exist.</p>';
  }
});

/*
 * QA checklist - Pouch OUT OF STOCK handling:
 * - When all variants unavailable → card is greyed out, label visible, not selectable
 * - Info icon still opens modal
 * - When at least one variant available → card selectable
 * - No layout shift
 * - No JS errors
 */
